import { Rule } from 'antd/lib/form';
import FormSelect from 'components/Select/FormSelect';
import { IItemForGetShortInfo, IGetCategoriesShort, IGetCategoriesShortResponse, ITeaserDishTransition } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import React, { useEffect, useMemo } from 'react';
import { getCategoriesShortInfo } from 'backend/categories';
import { useTranslation } from 'react-i18next';

interface IDishCategorySelect {
	selectedRestaurants: string[];
	dishTransition: Nullable<ITeaserDishTransition>;
	selectedTransitionDishCategory: Nullable<string>;
	setTransitionDishCategory: (value: Nullable<string>) => void;
	setFetchedDishCategories: (value: IItemForGetShortInfo[]) => void;
}

const DishCategorySelect: React.FC<IDishCategorySelect> = ({
	selectedRestaurants,
	dishTransition,
	selectedTransitionDishCategory,
	setTransitionDishCategory,
	setFetchedDishCategories,
}) => {
	const { response, loading, refetch, responseError } = useFetchData<
		IGetCategoriesShortResponse,
		IGetCategoriesShort
	>(selectedRestaurants.length ? getCategoriesShortInfo : null, {
		restaurantIds: selectedRestaurants,
	});
	const { t } = useTranslation('error');

	const validationRules = useMemo<Rule[]>(
		() => [
			{ required: true, message: t('selectCategory') },
			({ getFieldValue }) => ({
				validator(_, value) {
					// console.log(getFieldValue('selectedTransitionDishCategory'));
					if (value && !response?.data.categories.some((category) => category.id === value)) {
						return Promise.reject(new Error(t('categoryIsNotAvailableForAllSelectedRestaurants')));
					}
					return Promise.resolve();
				},
			}),
		],
		[t, response],
	);

	const categoriesList = useMemo(() => {
		if (
			!selectedTransitionDishCategory ||
			response?.data.categories.some((category) => category.id === selectedTransitionDishCategory)
		) {
			return response?.data.categories;
		}
		return response?.data.categories
			? [
					...response?.data.categories,
					{ id: dishTransition?.category.id || '', name: dishTransition?.category.name || '' },
			  ]
			: [];
	}, [response, selectedTransitionDishCategory]);

	useEffect(() => {
		response && setFetchedDishCategories(response.data.categories);
	}, [response]);

	useEffect(() => {
		refetch();
	}, [selectedRestaurants]);

	return (
		<FormSelect
			testId="DishCategorySelect"
			name="selectedTransitionDishCategory"
			label="categoryLabel"
			disabled={!selectedRestaurants.length || loading}
			options={categoriesList}
			fetchError={responseError}
			loading={loading}
			onRefetch={refetch}
			onChange={setTransitionDishCategory}
			validationRules={validationRules}
		/>
	);
};

export default React.memo(DishCategorySelect);
